import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Check, Minus, Plus, Trash2, MessageCircle } from "lucide-react";

const euro = (v) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);

const Stepper = ({ label, value, min, max, onChange, testId }) => (
    <div className="flex-1 bg-gray-50 rounded-xl p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">{label}</div>
        <div className="flex items-center justify-between">
            <button
                type="button"
                data-testid={`${testId}-minus`}
                onClick={() => onChange(Math.max(min, value - 1))}
                className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-nsp-green hover:border-nsp-green active:scale-95 transition-transform"
            >
                <Minus className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <span data-testid={`${testId}-value`} className="font-display text-2xl text-nsp-green">
                {value}
            </span>
            <button
                type="button"
                data-testid={`${testId}-plus`}
                onClick={() => onChange(Math.min(max, value + 1))}
                className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-nsp-green hover:border-nsp-green active:scale-95 transition-transform"
            >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
            </button>
        </div>
    </div>
);

export const QuoteItemDetail = ({ item, onClose, onSave, onRemove, mode = "quote" }) => {
    const p = item.product;
    const [days, setDays] = useState(item.days || 1);
    const [quantity, setQuantity] = useState(item.quantity || 1);
    const changed = days !== (item.days || 1) || quantity !== (item.quantity || 1);
    const isCatalog = mode === "catalog";

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                data-testid="quote-item-detail"
                className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto overscroll-contain rounded-3xl p-0 gap-0"
            >
                <div className="bg-gray-50 rounded-t-3xl p-4 flex items-center justify-center">
                    <img src={p.image} alt={p.name} className="h-40 object-contain" />
                </div>
                <div className="p-5 sm:p-6">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nsp-yellow-dark">
                        {p.category?.replace(/-/g, " ")}
                    </div>
                    <DialogTitle className="font-display text-2xl uppercase text-nsp-green tracking-tight mt-1">
                        {p.name}
                    </DialogTitle>
                    <p className="text-sm text-zinc-600 leading-relaxed mt-2">{p.description}</p>

                    {p.specs?.length > 0 && (
                        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {p.specs.map((s) => (
                                <li key={s} className="flex items-start gap-2 text-xs text-zinc-600">
                                    <Check className="w-3.5 h-3.5 text-nsp-green-vivid flex-shrink-0 mt-0.5" strokeWidth={3} />
                                    {s}
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                        {[["Giorno", p.daily], ["Settimana", p.weekly], ["Mese", p.monthly]].map(([l, v]) => (
                            <div key={l} className="bg-nsp-green-50 rounded-xl py-2.5">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{l}</div>
                                <div className="font-display text-lg text-nsp-green leading-none mt-0.5">
                                    {v ? euro(v) : "—"}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        data-testid="detail-ask-assistant"
                        onClick={() => {
                            onClose();
                            window.dispatchEvent(
                                new CustomEvent("nsp:open-chat", {
                                    detail: { prefill: `Ho una domanda su "${p.name}": ` },
                                })
                            );
                        }}
                        className="mt-4 w-full rounded-full border-2 border-nsp-green/25 bg-nsp-green-50/60 text-nsp-green px-5 py-3 text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2 hover:border-nsp-green hover:bg-white transition-colors active:scale-95"
                    >
                        <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
                        Chiedi all'assistente
                    </button>

                    <div className="mt-5 text-sm font-bold text-nsp-green">
                        {isCatalog
                            ? "Per quanti giorni e quanti pezzi ti serve?"
                            : "Vuoi modificare l'articolo o mantenerlo?"}
                    </div>
                    <div className="mt-3 flex gap-3">
                        <Stepper label="Giorni" value={days} min={1} max={365} onChange={setDays} testId="detail-days" />
                        <Stepper label="Quantità" value={quantity} min={1} max={20} onChange={setQuantity} testId="detail-qty" />
                    </div>

                    <div className={`mt-6 flex items-center gap-3 ${onRemove ? "justify-between" : "justify-end"}`}>
                        {onRemove && (
                            <button
                                type="button"
                                data-testid="detail-remove"
                                onClick={onRemove}
                                className="inline-flex items-center gap-2 text-red-600 text-xs font-bold uppercase tracking-wider hover:text-red-700 px-2 py-3"
                            >
                                <Trash2 className="w-4 h-4" /> Rimuovi
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="detail-keep"
                            onClick={() =>
                                isCatalog || changed ? onSave(days, quantity) : onClose()
                            }
                            className="rounded-full bg-nsp-green text-white px-7 py-3.5 font-bold uppercase tracking-widest text-xs hover:bg-nsp-green-dark shadow-md active:scale-95 transition-transform"
                        >
                            {isCatalog
                                ? "Aggiungi al preventivo"
                                : changed
                                  ? "Salva modifiche"
                                  : "Mantieni articolo"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default QuoteItemDetail;
